import type { CmmdProjectMilestone } from "@cmmd-forge/contracts";
import { ChevronDownIcon, ChevronRightIcon, MoreHorizontalIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import { ProjectTaskMilestoneIcon } from "./ProjectTaskMilestoneBoardIcons";

const milestoneDateFormatter = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

function formatMilestoneDate(value: string | null): string | null {
  if (value == null) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : milestoneDateFormatter.format(date);
}

interface ProjectTaskMilestoneLaneHeaderProps {
  readonly label: string;
  readonly status: CmmdProjectMilestone["status"] | null;
  readonly taskCount: number;
  readonly doneCount: number;
  readonly endDate: string | null;
  readonly isExpanded: boolean;
  readonly onToggle: () => void;
  readonly onManage?: (() => void) | undefined;
}

export function ProjectTaskMilestoneLaneHeader(props: ProjectTaskMilestoneLaneHeaderProps) {
  const dueLabel = formatMilestoneDate(props.endDate);
  return (
    <div className="relative flex min-h-touch w-full min-w-0 items-stretch border-b border-border md:min-h-11">
      <button
        type="button"
        onClick={props.onToggle}
        aria-expanded={props.isExpanded}
        aria-label={props.label}
        className="absolute inset-0 text-left transition-colors duration-150 hover:bg-[var(--task-board-card)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
      />
      <div
        className="pointer-events-none relative flex min-w-0 max-w-full shrink items-center gap-2.5 py-2 pl-3"
        aria-hidden
      >
        {props.isExpanded ? (
          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        ) : (
          <ChevronRightIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        )}
        <ProjectTaskMilestoneIcon status={props.status} />
        <span className="min-w-0 truncate text-sm font-semibold text-foreground">
          {props.label}
        </span>
      </div>
      {props.onManage != null ? (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          aria-label={`Edit milestone ${props.label}`}
          className={`relative z-10 self-center ${RESPONSIVE_ICON_BUTTON_CLASS}`}
          onClick={props.onManage}
        >
          <MoreHorizontalIcon className="size-4" aria-hidden />
        </Button>
      ) : null}
      <div className="pointer-events-none relative ml-auto flex shrink-0 items-center gap-2 px-3">
        {dueLabel != null ? (
          <span className="hidden text-sm text-muted-foreground sm:inline md:text-xs">
            Due {dueLabel}
          </span>
        ) : null}
        <span className="text-sm tabular-nums text-muted-foreground md:text-xs">
          {props.doneCount}/{props.taskCount}
        </span>
      </div>
    </div>
  );
}
